import React, { useState } from 'react';
import { User, Lock, Eye, EyeOff, Shield, Building } from 'lucide-react';

const Login = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Demo users - in production this would be from a database
  const users = [
    {
      username: 'admin',
      password: 'UMS2024!Admin',
      role: 'System Administrator',
      name: 'System Administrator',
      permissions: ['all']
    },
    {
      username: 'maria.rodriguez',
      password: 'Worker123!',
      role: 'Manufacturing Operator',
      name: 'Maria Rodriguez',
      permissions: ['assembly', 'inventory_view']
    },
    {
      username: 'james.chen',
      password: 'Worker123!',
      role: 'Manufacturing Operator', 
      name: 'James Chen',
      permissions: ['assembly', 'inventory_view']
    },
    {
      username: 'sarah.johnson',
      password: 'QC2024!',
      role: 'Quality Control Specialist',
      name: 'Sarah Johnson',
      permissions: ['quality_control', 'assembly', 'inventory_view', 'reports']
    },
    {
      username: 'dr.chen',
      password: 'QC2024!',
      role: 'Quality Assurance Manager',
      name: 'Dr. Michael Chen',
      permissions: ['quality_control', 'reports', 'inventory_view']
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    const user = users.find(u => 
      u.username === credentials.username && 
      u.password === credentials.password
    );

    if (user) {
      localStorage.setItem('currentUser', JSON.stringify(user));
      onLogin(user);
    } else {
      setError('Invalid credentials. Please verify your username and password.');
    }
    
    setIsLoading(false);
  };

  const handleInputChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Government-Style Header */}
        <div className="bg-white rounded-t-xl border-t-4 border-blue-600 shadow-lg p-8">
          {/* Official Logo and Branding */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                <div className="text-white font-bold text-2xl">+</div>
              </div>
              <div className="text-left">
                <div className="text-xl font-bold text-gray-800">UNIQUE MEDICAL SUPPLY LTD.</div>
                <div className="text-sm text-blue-600 font-semibold">HEALTH CANADA LICENSED</div>
                <div className="text-xs text-gray-500">Manufacturing License #12345</div>
              </div>
            </div>
            
            {/* System Title */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h1 className="text-lg font-bold text-gray-800 mb-1">Naloxone Kit Manufacturing System</h1>
              <p className="text-sm text-blue-700 mb-2">Regulatory Compliance & Traceability Platform</p>
              <div className="flex items-center justify-center text-xs text-green-700">
                <Shield className="h-3 w-3 mr-1" />
                <span>Secure Government-Grade System</span>
              </div>
            </div>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-r-lg">
                <div className="flex items-center">
                  <div className="text-sm font-medium">{error}</div>
                </div>
              </div>
            )}

            {/* Username Field */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Username
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  name="username"
                  value={credentials.username}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base bg-gray-50"
                  placeholder="Enter your system username"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={credentials.password}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-12 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base bg-gray-50"
                  placeholder="Enter your secure password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 px-4 rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-base shadow-md"
            >
              {isLoading ? 'Authenticating...' : 'Secure System Access'}
            </button>
          </form>
        </div>

        {/* Government Footer */}
        <div className="bg-gray-800 text-white rounded-b-xl p-4 shadow-lg">
          <div className="flex items-center justify-center text-xs mb-2">
            <Building className="h-3 w-3 mr-1" />
            <span>Government of Canada Compliant System</span>
          </div>
          <div className="text-xs text-center text-gray-300">
            © 2024 Unique Medical Supply Ltd. | Health Canada Licensed Facility
          </div>
          <div className="text-xs text-center text-gray-400 mt-1">
            All activities monitored and logged for regulatory compliance
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;